// Layout engine — pure function that computes SVG positions for the timeline grid.
// Ported from journey-a-timeline.jsx computeLayout() (L414-473).

import type { LaneDef, FrameDef } from '../../types/index.ts';

/** Resolve whether a lane is a "context" lane (not a branch lane). */
function isContextLane(lane: LaneDef): boolean {
  if (lane.isBranch !== undefined) return !lane.isBranch;
  return lane.group === 'context';
}

/** Get the lane group key for branch path matching. */
function laneGroupKey(lane: LaneDef): string {
  return lane.group ?? lane.id;
}

// ─── Layout Constants (from journey-a-timeline.jsx L390-396) ───
export const LANE_LABEL_W = 165;
export const FRAME_W = 195;
export const MIN_LANE_H = 56;
export const NODE_BOX_H = 30;
export const NODE_GAP = 4;
export const NODE_PAD = 6;
export const HEADER_H = 52;
/** A note band carries up to three lines of narration (Moment ADR-040 D3). */
export const NOTE_BOX_H = 44;

export interface NodePosition {
  x: number;
  y: number;
  w: number;
  h: number;
  laneId: string;
}

/** A note of a moment, placed in the lane it speaks for. */
export interface NotePosition {
  key: string;
  frameId: string;
  laneId: string;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutResult {
  visibleLanes: LaneDef[];
  visibleFrames: FrameDef[];
  laneHeights: Record<string, number>;
  laneYStart: Record<string, number>;
  nodePositions: Record<string, NodePosition>;
  notePositions: NotePosition[];
  totalHeight: number;
  totalWidth: number;
}

/**
 * Compute the full SVG layout from topology data and active branch paths.
 * Pure function — no React, no Redux, easily testable.
 */
export function computeLayout(
  allLanes: LaneDef[],
  allFrames: FrameDef[],
  activePaths: Set<string>,
): LayoutResult {
  // ── Filter visible lanes ──
  const visibleLanes = allLanes.filter((l) => isContextLane(l) || activePaths.has(laneGroupKey(l)));

  // ── Filter visible frames ──
  const visibleFrames = allFrames.filter((frame) => {
    // A moment with notes is a column even when it holds no node (Moment ADR-040 D3).
    if ((frame.notes?.length ?? 0) > 0) return true;
    return frame.nodes.some((n) => {
      const lane = allLanes.find((l) => l.id === n.lane);
      if (!lane) return false;
      if (isContextLane(lane)) {
        return !n.path || activePaths.has(n.path);
      }
      return activePaths.has(laneGroupKey(lane));
    });
  });

  // ── Build lane index ──
  const laneIdx: Record<string, number> = {};
  visibleLanes.forEach((l, i) => {
    laneIdx[l.id] = i;
  });

  // ── Resolve the lane each note speaks for (an unknown lane falls to the first) ──
  const noteLane = (note: { lane?: string }): string | undefined =>
    note.lane && laneIdx[note.lane] !== undefined ? note.lane : visibleLanes[0]?.id;

  // ── Compute the tallest stack (nodes, then notes) per lane across all frames ──
  const stackHeight = (nodeCount: number, noteCount: number): number => {
    const nodesH = nodeCount > 0 ? nodeCount * NODE_BOX_H + (nodeCount - 1) * NODE_GAP : 0;
    const notesH = noteCount > 0 ? noteCount * NOTE_BOX_H + (noteCount - 1) * NODE_GAP : 0;
    return nodesH + (nodeCount > 0 && noteCount > 0 ? NODE_GAP : 0) + notesH;
  };
  const laneMaxStack: Record<string, number> = {};
  visibleLanes.forEach((l) => {
    laneMaxStack[l.id] = NODE_BOX_H;
  });

  visibleFrames.forEach((frame) => {
    const countByLane: Record<string, number> = {};
    const notesByLane: Record<string, number> = {};
    frame.nodes.forEach((n) => {
      const lane = allLanes.find((l) => l.id === n.lane);
      if (!lane) return;
      if (isContextLane(lane) && n.path && !activePaths.has(n.path)) return;
      if (!isContextLane(lane) && !activePaths.has(laneGroupKey(lane))) return;
      countByLane[n.lane] = (countByLane[n.lane] || 0) + 1;
    });
    (frame.notes ?? []).forEach((note) => {
      const lid = noteLane(note);
      if (lid) notesByLane[lid] = (notesByLane[lid] || 0) + 1;
    });
    new Set([...Object.keys(countByLane), ...Object.keys(notesByLane)]).forEach((lid) => {
      const h = stackHeight(countByLane[lid] || 0, notesByLane[lid] || 0);
      if (h > (laneMaxStack[lid] || 0)) laneMaxStack[lid] = h;
    });
  });

  // ── Compute lane heights and Y positions ──
  const laneHeights: Record<string, number> = {};
  const laneYStart: Record<string, number> = {};
  let cumY = HEADER_H;

  visibleLanes.forEach((l) => {
    const h = Math.max(MIN_LANE_H, (laneMaxStack[l.id] || NODE_BOX_H) + NODE_GAP + NODE_PAD * 2);
    laneHeights[l.id] = h;
    laneYStart[l.id] = cumY;
    cumY += h;
  });

  // ── Compute node and note positions ──
  const nodePositions: Record<string, NodePosition> = {};
  const notePositions: NotePosition[] = [];

  visibleFrames.forEach((frame, fi) => {
    const nodesInLane: Record<string, typeof frame.nodes> = {};
    const notesInLane: Record<string, NonNullable<typeof frame.notes>> = {};

    frame.nodes.forEach((n) => {
      const lane = allLanes.find((l) => l.id === n.lane);
      if (!lane) return;
      if (isContextLane(lane) && n.path && !activePaths.has(n.path)) return;
      if (!isContextLane(lane) && !activePaths.has(laneGroupKey(lane))) return;
      if (!nodesInLane[n.lane]) nodesInLane[n.lane] = [];
      nodesInLane[n.lane].push(n);
    });
    (frame.notes ?? []).forEach((note) => {
      const lid = noteLane(note);
      if (!lid) return;
      if (!notesInLane[lid]) notesInLane[lid] = [];
      notesInLane[lid].push(note);
    });

    new Set([...Object.keys(nodesInLane), ...Object.keys(notesInLane)]).forEach((laneId) => {
      const nodes = nodesInLane[laneId] ?? [];
      const notes = notesInLane[laneId] ?? [];
      const lh = laneHeights[laneId];
      const totalH = stackHeight(nodes.length, notes.length);
      const startY = laneYStart[laneId] + (lh - totalH) / 2;

      nodes.forEach((n, ni) => {
        nodePositions[n.id] = {
          x: fi * FRAME_W,
          y: startY + ni * (NODE_BOX_H + NODE_GAP),
          w: FRAME_W - 12,
          h: NODE_BOX_H,
          laneId: n.lane,
        };
      });
      const notesY = startY + (nodes.length > 0 ? nodes.length * (NODE_BOX_H + NODE_GAP) : 0);
      notes.forEach((note, ni) => {
        notePositions.push({
          key: `${frame.id}::note::${notePositions.length}`,
          frameId: frame.id,
          laneId,
          text: note.text,
          x: fi * FRAME_W,
          y: notesY + ni * (NOTE_BOX_H + NODE_GAP),
          w: FRAME_W - 12,
          h: NOTE_BOX_H,
        });
      });
    });
  });

  return {
    visibleLanes,
    visibleFrames,
    laneHeights,
    laneYStart,
    nodePositions,
    notePositions,
    totalHeight: cumY,
    totalWidth: visibleFrames.length * FRAME_W + 20,
  };
}
